// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import getColor from "@insite/mobius/utilities/getColor";
import { checkoutGeneralStyle } from "@rugby/Overrides/Widgets/CheckoutShipping/Checkout.general.style";
import { screen } from "@rugby/XDL/Helpers";
import Edit from "@rugby/XDL/Icons/Edit";
import PlusRound from "@rugby/XDL/Icons/PlusRound";
import Trash from "@rugby/XDL/Icons/Trash";
import { ellipsis } from "@rugby/XDL/Utilities";
import CustomMediaQuery from "@rugby/XDL/Utilities/MediaQuery";
import { xFlexGap } from "@rugby/XDL/Utilities/SharedStyles";
import { css } from "styled-components";

const breakpointForCartLines: any = {
    xl: 1340,
    lg: 1160,
};

const MediaQuery = CustomMediaQuery(breakpointForCartLines);

const productPartNumbersDefaultStyle = {
    container: {
        css: css`
            padding: 0;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 21px;
            ${screen("lte", "md")`
                justify-content: flex-start;
                align-items: flex-start;
                text-align: left;
            `}
        `,
    },
    labelText: {
        css: css`
            font-weight: normal;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 21px;
            padding-right: 4px;
        `,
    },
    valueText: {
        css: css`
            flex-grow: 0;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 21px;
        `,
    },
};

const hideActionsContainer = css`
    .show-on-hover > *:not(.cartLinesNotes),
    .cartLinesNotes button {
        opacity: 0;
        -webkit-transition: opacity 300ms ease-in-out;
        -moz-transition: opacity 300ms ease-in-out;
        -ms-transition: opacity 300ms ease-in-out;
        -o-transition: opacity 300ms ease-in-out;
        transition: opacity 300ms ease-in-out;
    }

    .cartLine-actions {
        position: relative;
        min-height: 20px;
        flex-wrap: wrap;

        p,
        textarea {
            opacity: 0;
            visibility: hidden;
        }
    }
`;

const showActionsContainer = css`
    .show-on-hover,
    .show-on-hover > *,
    .cartLinesNotes button {
        opacity: 1;
    }
    .cartLine-actions {
        position: relative;
        min-height: 20px;
        max-height: none;
        transition: max-height 0.25s ease-in;

        p,
        textarea {
            visibility: visible;
            opacity: 1;
        }
    }
`;

const mainContainerHoverMode = css`
    ${hideActionsContainer}

    &.cart-line-item {
        ${screen("lte", "sm")`
            ${showActionsContainer}
            && {
                background-color: transparent;
            }
        `};
    }

    &.hover-mode {
        background-color: ${getColor("common.ui3")};
        cursor: pointer;
        border-top-color: transparent;
        border-bottom-color: transparent;
        & + div {
            border-top-color: transparent;
        }

        ${showActionsContainer}
    }
    &&& textArea.show-notes {
        opacity: 1;
        visibility: visible;
    }
`;

// separator between cartLines
const separatorStyle = css`
    border-top: 1px solid ${getColor("common.border")};
    &:first-child {
        border-top: none;
    }
    &:last-child {
        border-bottom: 1px solid ${getColor("common.border")};
    }
`;

const quantityInputHeight = 34;

// Left
const productInfoLeftStyle = {
    productInfoContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            flex: 1 0 50%;
            ${MediaQuery.lte("lg")} {
                flex: 1 0 auto;
                margin-bottom: 10px;
            }

            ${screen("lte", "xs")`
                flex-direction: column;
                justify-content: center;
                align-items: center;
                text-align: center;
            `}
        `,
    },
    productImageContainer: {
        css: css`
            width: 116px;
            height: 92px;
            min-width: 116px;
            min-height: 92px;
            margin-right: 20px;
            background: ${getColor("common.white")};
            ${screen("lte", "xs")`
                margin-right: 0;
                margin-bottom: 10px;
            `}
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 100%;
                height: 100%;

                > span {
                    width: 100%;
                    height: 100%;
                }
            `,
        },
        image: {
            css: css`
                width: 100%;
                img {
                    width: auto;
                    height: auto;
                    max-width: 100%;
                    max-height: 100%;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                }

                p {
                    ${ellipsis(3, 23)}
                    max-width: 100%;
                    max-height: 100%;
                }
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            color: "secondary.main",
            css: css`
                min-height: 37px;
                margin-bottom: 7px;
                display: inline-flex;
                ${screen("lte", "md")`
                    margin-bottom: 12px;
                `}
            `,
            typographyProps: {
                color: "secondary.main",
                size: 16,
                css: css`
                    width: 100%;
                    line-height: 18px;
                    letter-spacing: 0;

                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    max-height: calc(4 * 18px);
                    -webkit-line-clamp: 2;

                    ${wrapInContainerStyles}

                    @media print {
                        max-height: none;
                        text-overflow: unset;
                        overflow: visible;
                        -webkit-line-clamp: none;
                        -webkit-box-orient: unset;
                        overflow-wrap: unset;
                        word-wrap: unset;
                        word-break: unset;
                    }
                `,
            },
        },
    },
    productBrandAndDescriptionContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-direction: column;
            flex: 1;
            width: 100%;
            padding-right: 8px;
            ${screen("lte", "md")`
                padding-right: 0;
            `}
        `,
    },
    productBrand: {
        nameLink: {
            typographyProps: {
                color: "secondary",
                css: css`
                    color: ${getColor("text.main")} !important;
                    font-weight: normal;
                    font-size: 14px;
                    letter-spacing: 0;
                    line-height: 21px;
                `,
            },
        },
        nameText: {
            color: "secondary",
        },
    },
    productProductNumberAndAvailabilityContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            align-items: flex-start;
            ${MediaQuery.lte("xl")} {
                flex-direction: column;
            }

            > div {
                min-width: 200px;
                ${MediaQuery.lt(1380)} {
                    max-width: none;
                }
            }

            @media print {
                flex-direction: column;
                max-width: none;
            }
        `,
    },
    promotionNameText: {},
    productPartNumbersStyles: {
        container: {
            gap: 0,
            css: css`
                flex: 1;
                padding-right: 6px;
                ${MediaQuery.lte("xl")} {
                    margin-bottom: 8px;
                }
            `,
        },
        erpNumberGridItem: productPartNumbersDefaultStyle.container,
        customerNameGridItem: productPartNumbersDefaultStyle.container,
        manufacturerItemGridItem: productPartNumbersDefaultStyle.container,
        erpNumberLabelText: productPartNumbersDefaultStyle.labelText,
        customerNameLabelText: productPartNumbersDefaultStyle.labelText,
        manufacturerItemLabelText: productPartNumbersDefaultStyle.labelText,
        erpNumberValueText: productPartNumbersDefaultStyle.valueText,
        customerNameValueText: productPartNumbersDefaultStyle.valueText,
        manufacturerItemValueText: productPartNumbersDefaultStyle.valueText,
    },
    productAvailability: {
        messageGridItem: {
            css: css`
                justify-content: center;
                ${screen("lte", "md")`
                    justify-content: flex-start;
                `}
            `,
        },
        messageText: {
            size: 14,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
    },
    cartLineErrorMessageText: { color: "danger" },
};

// Right
const productInfoRightStyle = {
    productInfoRightContainer: {
        css: css`
            flex: 0 1 50%;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            display: flex;
            font-size: 12px;
            ${MediaQuery.lte("lg")} {
                flex: 1 0 auto;
                align-items: flex-end;
            }
        `,
    },
    productPriceContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 24%;

            &.cartline-priceUOM {
                padding-top: 8px;
                ${screen("lte", "sm")`
                    order: 4;
                `}
            }
        `,
    },
    productPriceUOM: {
        wrapper: {
            css: css`
                padding-top: 8px;
                font-size: 15px;
                letter-spacing: 0;
                line-height: 21px;
                min-width: 83px;
                text-align: center;

                ${MediaQuery.lte("lg")} {
                    text-align: left;
                    width: 100%;
                }
            `,
        },
        price: {
            priceText: {
                css: css`
                    letter-spacing: 0;
                    line-height: 21px;
                    font-size: 15px;
                `,
            },
        },
        priceLabelText: {
            css: css`
                display: none;
                ${MediaQuery.lte("lg")} {
                    display: block;
                }
            `,
        },
    },
    productPrice: {
        wrapper: {
            css: css`
                padding-top: 8px;
                font-size: 15px;
                letter-spacing: 0;
                line-height: 21px;
                min-width: 83px;
                text-align: center;

                ${MediaQuery.lte("lg")} {
                    text-align: left;
                    width: 100%;
                }
            `,
        },
        price: {
            priceText: {
                css: css`
                    letter-spacing: 0;
                    line-height: 21px;
                    font-size: 15px;
                    font-weight: normal;
                `,
            },
        },
        priceLabelText: {
            css: css`
                display: none;
                ${MediaQuery.lte("lg")} {
                    display: block;
                }
            `,
        },
    },
    totalLabelText: {
        css: css`
            display: none;
            ${MediaQuery.lte("lg")} {
                display: block;
            }
        `,
    },
    productNotAvailable: {
        css: css`
            display: flex;
            justify-content: flex-end;
            color: ${getColor("warning.main")};
        `,
    },
    quantityAndRemoveContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: center;
            height: ${quantityInputHeight}px;
            align-items: flex-start;
            ${screen("gte", "md")`
                min-width: 24%;
            `}
        `,
    },
    quantity: {
        readOnlyQuantityText: {
            extendedStyles: {
                heading: {
                    variant: "legend",
                    css: css`
                        display: none;
                        ${MediaQuery.lte("lg")} {
                            display: block;
                        }
                    `,
                },
                text: {
                    css: css`
                        ${screen("gte", "md")`
                            text-align: center;
                        `}
                    `,
                },
            },
        },
        editableQuantityTextField: {
            cssOverrides: {
                formField: css`
                    margin-right: 2px;
                    height: ${quantityInputHeight}px;
                    width: 76px;
                `,
                inputSelect: css`
                    height: ${quantityInputHeight}px;
                `,
            },
        },
    } as CartLineQuantityStyles,
    removeCartLine: {
        css: css`
            position: absolute;
            top: 10px;
            right: 0;
        `,
    },
    extendedUnitNetPriceContainer: {
        css: css`
            padding-top: 8px;
            min-width: 46px;
            text-align: center;
            ${screen("gte", "md")`
                min-width: 24%;
                align-items: center;
            `}
            ${screen("lte", "sm")`
                text-align: left;
            `}
        `,
    },
    extendedUnitNetPriceText: {
        size: 15,
        color: "text.main",
        lineHeight: "21px",
        css: css`
            letter-spacing: 0;
            text-align: right;
            font-weight: bold;
            ${screen("lte", "md")`
                padding-right: 0;
           `}
        `,
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    removeCartLineIcon: {
        src: Trash,
        color: "secondary.main",
        css: css`
            width: 12px;
            height: 16px;
        `,
    },
    configurationOptionText: {},
};

// Actions - for Extended
const cartLineActionsStyle = {
    cartLineNotes: {
        wrapper: {
            css: css`
                flex: 0 0 auto;
                &&& {
                    ${xFlexGap(10)}
                }
                align-items: center;
                display: flex;
                max-width: 100%;
                flex-wrap: wrap;

                ${MediaQuery.lte(520)} {
                    max-width: 100%;
                }
            `,
        },
        editableNotesTextArea: {
            ...checkoutGeneralStyle.notesTextAreaOneRow,
        },
        toggleLink: {
            color: "secondary",
            icon: {
                iconProps: {
                    src: Edit,
                    color: "secondary",
                    size: 14,
                },
            },
            css: css`
                font-size: 14px;
                letter-spacing: 0;
                line-height: 17px;
                margin-bottom: 0;
                flex-shrink: 0;
                width: auto;
            `,
        },
    },
    addToListLink: {
        color: "secondary",
        icon: {
            iconProps: {
                src: PlusRound,
                color: "secondary",
                size: 12,
            },
        },
        css: css`
            flex: 1;
            justify-content: flex-end;
            align-items: center;
        `,
        typographyProps: {
            size: 14,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
    },
    costCodeGridItem: {
        css: css``,
    },
    costCodeLabelText: {
        css: css``,
    },
    addToListContainer: {
        css: css`
            position: absolute;
            right: 8px;
            min-width: auto;
        `,
    },
    cartLineActions: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin-top: 8px;
            padding-left: 2px;

            ${screen("lte", "md")`
                margin-top: 14px;
            `}
        `,
    },
    cartLineContainer: {
        css: css`
            width: 90%;
        `,
    },
};

// cost code Grid Item added in 5.0.13 just hiding for now as it wasn't part of spec.
const costCodeGridItem = {
    css: css`
        display: none;
    `,
};

// containersStyle
const containersStyle = {
    mainWrapper: {
        css: css`
            margin-top: 30px;
            @media print {
                break-inside: avoid;
            }
        `,
    },
    gridLineWrapper: {
        css: css`
            @media print {
                break-inside: avoid;
            }
        `,
    },
    mainContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 10px 8px 10px 10px;
            ${separatorStyle}
            ${mainContainerHoverMode}

            ${screen("lte", "sm")`
                position: relative;

                .show-on-hover,
                .show-on-hover > * {
                    opacity: 1;
                }
                .cartLine-actions {
                    max-height: none;
                    transition: max-height 0.25s ease-in;

                    p,
                    textarea {
                        visibility: visible;
                        opacity: 1;
                    }
                }
            `}

            @media print {
                break-inside: avoid;
            }
        `,
    },
    // container for cartLine without actions
    container: {
        css: css`
            display: flex;
            position: relative;

            ${MediaQuery.lte("lg")} {
                flex-direction: column;
            }
        `,
    },

    titleText: {
        css: css`
            font-weight: 400;
        `,
    },
};

const cartLineStyles = {
    ...containersStyle,
    ...productInfoLeftStyle,
    ...productInfoRightStyle,
    ...cartLineActionsStyle,
    flexColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 34px;
            ${screen("lte", "md")`
                margin-top: 0;
            `}
            &.cartline-priceUOM {
                padding-top: 8px;
                ${screen("lte", "sm")`
                    order: 4;
                `}
            }
        `,
    },
    columnItem: {
        css: css`
            display: flex;
            flex-direction: column;
            ${screen("gte", "sm")`
                width: 25% !important;
            `}
            padding: 0 !important;
        `,
    },
    priceContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            ${screen("gte", "md")`
                min-width: 24%;
                align-items: center;
            `}
        `,
    },
    customColumnItem: {
        css: css`
            display: flex;
            flex-direction: column;
            ${screen("gte", "sm")`
                width: 19% !important;
            `}
            padding: 0 !important;
        `,
    },
    // XNG change: added styles for minPack
    minPackQuantityLabelText: {
        css: css`
            font-size: 14px;
            padding-right: 4px;
            &::after {
                content: ":";
            }
        `,
    },
    minPackQuantityValueText: {
        css: css`
            font-size: 14px;
        `,
    },
    qtyBreakPrice: {
        wraper: {
            css: css`
                border: 1px solid ${getColor("common.border")};
                padding: 4px 8px;
                margin: 8px 0;
            `,
        },
    },
    quantityBreakPrice: {
        css: css`
            max-width: 233px;
        `,
    },
    rightContainer: {
        css: css`
            flex: 0 1 50%;
            display: flex;
            flex-direction: column;
        `,
    },
};

export default cartLineStyles;
